// ─── HubRemovedCard (presentational) ──────────────────────────────────────────
// Renders the personalized "What Your Home WTR Hub Removed" card from data the
// PARENT fetched (WTRHubScreen owns the single /removed-contaminants fetch so the
// event log is written once). Three display modes from the capability frame:
//   computed   (Tier A, influent >= 10x lab floor): level -> output + lab %
//   capability (Tier A, below 10x floor): level + lab % as capability, no output
//   mechanism  (Tier B): "Removed via [stage]", never a percentage
// Falls back to a generic capabilities note on error or when no install exists.

const C = {
  navy: "#0F1419", header: "#0A1A2E", blue: "#51B0E6",
  green: "#3D7058", red: "#B84A4A", muted: "#A6A8AB",
  border: "rgba(15, 20, 25, 0.06)",
};

function prettyUnit(u) {
  switch ((u || "").toUpperCase().trim()) {
    case "UG/L": return "\u00B5g/L";
    case "MG/L": return "mg/L";
    case "NG/L": return "ng/L";
    case "PPT": return "ppt";
    case "PPB": return "ppb";
    case "PCI/L": return "pCi/L";
    case "NTU": return "NTU";
    default: return u || "";
  }
}

function pctText(entry) {
  const p = entry.removal_pct;
  if (p == null) return "";
  return `${entry.above_threshold === ">" ? ">" : ""}${p}%`;
}

function RemovalBadge({ children, tone = "verified" }) {
  const styles = tone === "verified"
    ? { color: C.green, bg: "rgba(74, 138, 111, 0.10)", bd: "rgba(74, 138, 111, 0.20)" }
    : { color: "#1F6FA0", bg: "rgba(81, 176, 230, 0.10)", bd: "rgba(81, 176, 230, 0.22)" };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, color: styles.color, background: styles.bg, padding: "3px 8px", borderRadius: 6, border: `1px solid ${styles.bd}`, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function ContaminantRow({ entry, last }) {
  const unit = prettyUnit(entry.detected_unit);
  const level = entry.detected_level;
  const isMechanism = entry.tier === "verified_mechanism";

  let rightSide;
  if (isMechanism) {
    rightSide = <RemovalBadge tone="mechanism">{"\u2713"} Removed via {entry.filter_stage}</RemovalBadge>;
  } else if (entry.display_mode === "computed") {
    rightSide = (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>
          <span style={{ color: C.red }}>{level}</span>
          <span style={{ margin: "0 4px" }}>{"\u2192"}</span>
          <span style={{ color: C.green }}>{entry.output_level} {unit}</span>
        </span>
        <RemovalBadge>{"\u2713"} {pctText(entry)} Lab Verified</RemovalBadge>
      </div>
    );
  } else {
    rightSide = (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.red }}>{level} {unit}</span>
        <RemovalBadge>{"\u2713"} {pctText(entry)} Lab Verified</RemovalBadge>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: last ? "none" : `1px solid ${C.border}`, gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{entry.name}</div>
        <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
          {entry.category}
          {entry.display_mode === "capability" ? " \u00B7 below detection in your water" : ""}
        </div>
      </div>
      <div style={{ flexShrink: 0 }}>{rightSide}</div>
    </div>
  );
}

function FooterStrip() {
  return (
    <div style={{ padding: "16px 18px", borderTop: `1px solid ${C.border}`, background: "rgba(255, 255, 255, 0.35)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginBottom: 10 }}>
        {[
          { label: "TAP", color: "rgba(15, 20, 25, 0.10)", text: "#A6A8AB" },
          { label: "CP", color: "#51B0E6", text: "#FFF" },
          { label: "RO", color: "#1F6FA0", text: "#FFF" },
          { label: "TC", color: "#C89B3C", text: "#FFF" },
          { label: "ALK", color: "#8B6FC0", text: "#FFF" },
          { label: "PURE", color: "#4A8A6F", text: "#FFF" },
        ].map((s, i, arr) => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: s.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, color: s.text, letterSpacing: 0.3 }}>{s.label}</div>
            {i < arr.length - 1 && <span style={{ color: "rgba(15, 20, 25, 0.20)", fontSize: 10 }}>{"\u2192"}</span>}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
        {[["1,000+", "Contaminants"], ["99%+", "PFAS"], ["99%+", "Heavy Metals"], ["Ca+Mg", "Remineralized"]].map(([val, label]) => (
          <div key={label} style={{ textAlign: "center", background: "rgba(81, 176, 230, 0.10)", borderRadius: 8, padding: "8px 4px", border: "1px solid rgba(81, 176, 230, 0.20)" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#51B0E6" }}>{val}</div>
            <div style={{ fontSize: 7, color: C.muted, marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Props: data (the /removed-contaminants response | null), loading (bool), errored (bool)
export default function HubRemovedCard({ data, loading, errored }) {
  const contaminants = data?.contaminants || [];
  const tierA = contaminants.filter(c => c.tier === "verified_sgs");
  const tierB = contaminants.filter(c => c.tier === "verified_mechanism");
  const utilityLabel = data?.installation?.pws_name
    ? data.installation.pws_name.toUpperCase()
    : "YOUR HOME WATER";

  const TIER_B_CAP = 8;
  const tierBShown = tierB.slice(0, TIER_B_CAP);
  const tierBMore = Math.max(0, tierB.length - TIER_B_CAP);

  return (
    <div className="card-default" style={{ overflow: "hidden", padding: 0 }}>
      <div style={{ background: C.header, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#FFFFFF" }}>What Gets Removed</div>
        <div style={{ fontSize: 9, fontWeight: 600, color: "#51B0E6", letterSpacing: 0.5, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{utilityLabel}</div>
      </div>

      <div style={{ padding: "0 18px" }}>
        {loading && (
          [0, 1, 2, 3].map(i => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ width: "40%", height: 26, borderRadius: 6, background: "rgba(15, 20, 25, 0.06)", animation: "hubShimmer 1.5s ease-in-out infinite" }} />
              <div style={{ width: "30%", height: 20, borderRadius: 6, background: "rgba(15, 20, 25, 0.06)", animation: "hubShimmer 1.5s ease-in-out infinite" }} />
            </div>
          ))
        )}

        {!loading && (errored || !data) && (
          <div style={{ padding: "16px 0", fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
            Personalized data unavailable, showing general capabilities. Your Home WTR Hub is engineered to remove 1,000+ contaminants across PFAS, heavy metals, pesticides, and microbiological categories.
          </div>
        )}

        {!loading && !errored && data && contaminants.length === 0 && (
          <div style={{ padding: "18px 0", textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 6 }}>No high-concern contaminants in your source water</div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
              Your Home WTR Hub continues to guard against 1,000+ contaminants across PFAS, heavy metals, pesticides, and microbiological categories.
            </div>
          </div>
        )}

        {!loading && !errored && tierA.length > 0 && tierA.map((c, i) => (
          <ContaminantRow key={c.key} entry={c} last={i === tierA.length - 1 && tierB.length === 0} />
        ))}

        {!loading && !errored && tierB.length > 0 && (
          <div style={{ padding: "14px 0 4px", borderTop: tierA.length > 0 ? `1px solid ${C.border}` : "none" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Also removed via filtration</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {tierBShown.map(c => (
                <span key={c.key} style={{ fontSize: 11, fontWeight: 600, color: "#1F6FA0", background: "rgba(81, 176, 230, 0.08)", padding: "4px 9px", borderRadius: 8, border: "1px solid rgba(81, 176, 230, 0.18)" }}>{c.name}</span>
              ))}
              {tierBMore > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, padding: "4px 9px" }}>+{tierBMore} more</span>
              )}
            </div>
          </div>
        )}
      </div>

      <FooterStrip />
    </div>
  );
}
